import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  useColorModeValue,
  Icon,
  SimpleGrid,
  Card,
  CardBody,
  Select,
  Checkbox,
  CheckboxGroup,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  useToast,
  Badge,
  Flex,
  Container,
  Progress,
  Divider,
  useBreakpointValue
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { colors } from '../../assets/styles/colors';
import {
  FiGlobe,
  FiTarget,
  FiClock,
  FiHeart,
  FiBriefcase,
  FiBook,
  FiMapPin,
  FiUsers,
  FiHome,
  FiZap,
  FiTrendingUp,
  FiStar
} from 'react-icons/fi';
import {
  LanguageGoals as LanguageGoalsType,
  LearningPurpose,
  TimeCommitment,
  LEARNING_PURPOSE_LABELS,
  TIME_COMMITMENT_LABELS
} from '../../types/onboarding';
import { languageCodeMap } from '../../i18n';

interface LanguageGoalsProps {
  onNext: (data?: any) => void;
  onPrevious: () => void;
  stepData: Record<string, any>;
  isLoading: boolean;
}

const LanguageGoals: React.FC<LanguageGoalsProps> = ({ onNext, stepData, isLoading }) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();

  // Theme colors with gaming-inspired gradients
  const bgGradient = useColorModeValue(
    'linear(to-br, gray.50, blue.50, purple.50)',
    'linear(to-br, gray.900, blue.900, purple.900)'
  );
  const cardBg = useColorModeValue('white', colors.primary[700]);
  const cardHoverBg = useColorModeValue('blue.50', colors.primary[600]);
  const selectedCardBg = useColorModeValue('blue.100', colors.primary[500]);
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('blue.500', 'blue.400');
  const successColor = useColorModeValue('green.500', 'green.400');

  // Responsive values
  const cardColumns = useBreakpointValue({ base: 1, md: 2, lg: 3 });
  const purposeColumns = useBreakpointValue({ base: 1, md: 2, lg: 4 });

  // Form state - removed fluencyTimeline, motivationLevel, and specificGoals
  const [targetLanguage, setTargetLanguage] = useState(() => {
    // First check if we have saved language goals
    if (stepData.languageGoals?.target_language) {
      return stepData.languageGoals.target_language;
    }

    // Set intelligent default based on native language
    const currentLang = i18n.language;
    if (currentLang === 'he') return 'english'; // Hebrew speakers likely want English
    if (currentLang === 'nl') return 'english'; // Dutch speakers likely want English
    if (currentLang === 'en') return 'dutch';   // English speakers can try Dutch

    return 'english'; // Safe default
  });
  const [nativeLanguage, setNativeLanguage] = useState(() => {
    // First check if we have saved language goals
    if (stepData.languageGoals?.native_language) {
      return stepData.languageGoals.native_language;
    }

    // Otherwise, derive from current UI language
    const currentLang = i18n.language;
    const languageMap: Record<string, string> = {
      'en': 'english',
      'nl': 'dutch',
      'he': 'hebrew'
    };

    return languageMap[currentLang] || 'english';
  });
  const [learningPurposes, setLearningPurposes] = useState<LearningPurpose[]>(() => {
    // First check if we have saved learning goals
    if (stepData.languageGoals?.learning_purpose && stepData.languageGoals.learning_purpose.length > 0) {
      return stepData.languageGoals.learning_purpose;
    }

    // Set default to personal_interest to make form immediately valid
    return ['personal_interest'];
  });
  const [timeCommitment, setTimeCommitment] = useState<TimeCommitment>(
    stepData.languageGoals?.time_commitment || '30_min_daily'
  );

  // Supported languages for learning (target languages) - only our supported languages
  const supportedTargetLanguages = [
    { value: 'english', label: 'English', flag: '🇺🇸', difficulty: 'Medium', color: 'blue' },
    { value: 'hebrew', label: 'Hebrew', flag: '🇮🇱', difficulty: 'Hard', color: 'cyan' },
    { value: 'dutch', label: 'Dutch', flag: '🇳🇱', difficulty: 'Medium', color: 'orange' }
  ];

  // Supported native languages (with UI translation support)
  const supportedNativeLanguages = [
    { value: 'english', label: 'English', flag: '🇺🇸' },
    { value: 'hebrew', label: 'Hebrew', flag: '🇮🇱' },
    { value: 'dutch', label: 'Dutch', flag: '🇳🇱' }
  ];

  // Handle native language change and update interface language
  const handleNativeLanguageChange = (selectedLanguage: string) => {
    setNativeLanguage(selectedLanguage);

    // Change interface language if supported
    const languageCode = languageCodeMap[selectedLanguage];
    if (languageCode && languageCode !== i18n.language) {
      i18n.changeLanguage(languageCode);

      // Save to localStorage for persistence
      localStorage.setItem('i18nextLng', languageCode);

      toast({
        title: t('onboarding.goals.languageChanged'),
        description: t('onboarding.goals.interfaceUpdated'),
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    }
  };

  const purposeOptions = [
    { value: 'travel' as LearningPurpose, icon: FiMapPin, color: 'blue', gradient: 'linear(to-r, blue.400, cyan.400)' },
    { value: 'business' as LearningPurpose, icon: FiBriefcase, color: 'purple', gradient: 'linear(to-r, purple.400, pink.400)' },
    { value: 'academic' as LearningPurpose, icon: FiBook, color: 'green', gradient: 'linear(to-r, green.400, teal.400)' },
    { value: 'personal_interest' as LearningPurpose, icon: FiHeart, color: 'pink', gradient: 'linear(to-r, pink.400, red.400)' },
    { value: 'professional_development' as LearningPurpose, icon: FiTrendingUp, color: 'orange', gradient: 'linear(to-r, orange.400, yellow.400)' },
    { value: 'family_heritage' as LearningPurpose, icon: FiHome, color: 'teal', gradient: 'linear(to-r, teal.400, green.400)' },
    { value: 'relocation' as LearningPurpose, icon: FiGlobe, color: 'cyan', gradient: 'linear(to-r, cyan.400, blue.400)' },
    { value: 'cultural_exploration' as LearningPurpose, icon: FiUsers, color: 'yellow', gradient: 'linear(to-r, yellow.400, orange.400)' }
  ];

  const timeCommitmentOptions = [
    { value: '15_min_daily' as TimeCommitment, icon: FiZap, label: '15 min/day', subtitle: 'Quick & Easy', color: 'green' },
    { value: '30_min_daily' as TimeCommitment, icon: FiTarget, label: '30 min/day', subtitle: 'Recommended', color: 'blue' },
    { value: '1_hour_daily' as TimeCommitment, icon: FiStar, label: '1 hour/day', subtitle: 'Intensive', color: 'purple' },
    { value: '2_hours_daily' as TimeCommitment, icon: FiTrendingUp, label: '2+ hours/day', subtitle: 'Power User', color: 'orange' },
    { value: 'weekends_only' as TimeCommitment, icon: FiClock, label: 'Weekends', subtitle: 'Flexible', color: 'teal' },
    { value: 'flexible' as TimeCommitment, icon: FiHeart, label: 'Flexible', subtitle: 'As I can', color: 'pink' }
  ];

  const handleNext = () => {
    if (!targetLanguage) {
      toast({
        title: t('onboarding.goals.validation.targetLanguageRequired'),
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (learningPurposes.length === 0) {
      toast({
        title: t('onboarding.goals.validation.purposeRequired'),
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    const languageGoals: LanguageGoalsType = {
      target_language: targetLanguage,
      native_language: nativeLanguage,
      learning_purpose: learningPurposes,
      time_commitment: timeCommitment,
      // Removed fields: fluency_timeline, specific_goals, motivation_level
      fluency_timeline: '6_months', // Default value for backend compatibility
      specific_goals: [], // Default empty array for backend compatibility
      motivation_level: 4 as 1 | 2 | 3 | 4 | 5 // Default value for backend compatibility
    };

    onNext({ languageGoals });
  };

  const selectedTargetLang = supportedTargetLanguages.find(l => l.value === targetLanguage);
  const completionPercentage = Math.round(
    ((targetLanguage ? 1 : 0) + (learningPurposes.length > 0 ? 1 : 0) + 1) / 3 * 100
  );

  return (
    <Box minH="100vh" bgGradient={bgGradient}>
      <Container maxW="6xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Gaming-style Header */}
          <VStack spacing={6} textAlign="center">
            <Box position="relative">
              <Icon
                as={FiTarget}
                fontSize="4xl"
                color={accentColor}
                filter="drop-shadow(0 0 20px rgba(59, 130, 246, 0.5))"
              />
              <Box
                position="absolute"
                top="-2"
                right="-2"
                w="6"
                h="6"
                bg={successColor}
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize="xs" fontWeight="bold" color="white">
                  2
                </Text>
              </Box>
            </Box>

            <VStack spacing={3}>
              <Heading
                size="xl"
                color={textColor}
                bgGradient="linear(to-r, blue.400, purple.500)"
                bgClip="text"
                fontWeight="black"
              >
                {t('onboarding.goals.title')}
              </Heading>
              <Text color={mutedTextColor} fontSize="lg" maxW="2xl">
                {t('onboarding.goals.subtitle')}
              </Text>
            </VStack>
          </VStack>

          {/* Language Selection Cards */}
          <SimpleGrid columns={cardColumns} spacing={6}>
            {/* Target Language */}
            <Card
              bg={cardBg}
              shadow="xl"
              borderRadius="2xl"
              border="2px"
              borderColor={targetLanguage ? accentColor : 'transparent'}
              transition="all 0.3s ease"
              _hover={{ transform: 'translateY(-4px)', shadow: '2xl' }}
            >
              <CardBody p={6}>
                <VStack spacing={4} align="stretch">
                  <HStack spacing={3}>
                    <Icon as={FiGlobe} color={accentColor} fontSize="xl" />
                    <Text fontWeight="bold" color={textColor}>
                      {t('onboarding.goals.targetLanguage')}
                    </Text>
                    <Badge colorScheme="red" variant="solid">Required</Badge>
                  </HStack>

                  <Select
                    placeholder={t('onboarding.goals.selectTargetLanguage')}
                    value={targetLanguage}
                    onChange={(e) => setTargetLanguage(e.target.value)}
                    size="lg"
                    borderRadius="xl"
                    border="2px"
                    borderColor={useColorModeValue('gray.200', 'gray.600')}
                    _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}
                  >
                    {supportedTargetLanguages.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.flag} {lang.label} ({lang.difficulty})
                      </option>
                    ))}
                  </Select>

                  {selectedTargetLang && (
                    <Box
                      p={3}
                      bg={selectedCardBg}
                      borderRadius="lg"
                      border="1px"
                      borderColor={`${selectedTargetLang.color}.200`}
                    >
                      <HStack justify="space-between">
                        <Text fontSize="sm" fontWeight="medium">
                          {selectedTargetLang.flag} {selectedTargetLang.label}
                        </Text>
                        <Badge colorScheme={selectedTargetLang.color} variant="subtle">
                          {selectedTargetLang.difficulty}
                        </Badge>
                      </HStack>
                    </Box>
                  )}
                </VStack>
              </CardBody>
            </Card>

            {/* Native Language */}
            <Card
              bg={cardBg}
              shadow="xl"
              borderRadius="2xl"
              transition="all 0.3s ease"
              _hover={{ transform: 'translateY(-4px)', shadow: '2xl' }}
            >
              <CardBody p={6}>
                <VStack spacing={4} align="stretch">
                  <HStack spacing={3}>
                    <Icon as={FiHome} color={accentColor} fontSize="xl" />
                    <Text fontWeight="bold" color={textColor}>
                      {t('onboarding.goals.nativeLanguage')}
                    </Text>
                  </HStack>

                  <Select
                    value={nativeLanguage}
                    onChange={(e) => handleNativeLanguageChange(e.target.value)}
                    size="lg"
                    borderRadius="xl"
                    border="2px"
                    borderColor={useColorModeValue('gray.200', 'gray.600')}
                    _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}
                  >
                    {supportedNativeLanguages.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.flag} {lang.label}
                      </option>
                    ))}
                  </Select>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>

          <Divider />

          {/* Learning Purposes */}
          <VStack spacing={6} align="stretch">
            <VStack spacing={3} textAlign="center">
              <HStack spacing={3}>
                <Icon as={FiHeart} color={accentColor} fontSize="2xl" />
                <Heading size="lg" color={textColor}>
                  {t('onboarding.goals.learningPurpose')}
                </Heading>
                <Badge colorScheme="red" variant="solid">Required</Badge>
              </HStack>
              <Text color={mutedTextColor}>
                {t('onboarding.goals.selectMultiplePurposes')}
              </Text>
            </VStack>

            <CheckboxGroup
              value={learningPurposes}
              onChange={(values) => setLearningPurposes(values as LearningPurpose[])}
            >
              <SimpleGrid columns={purposeColumns} spacing={4}>
                {purposeOptions.map((purpose) => {
                  const isSelected = learningPurposes.includes(purpose.value);
                  return (
                    <Card
                      key={purpose.value}
                      bg={isSelected ? selectedCardBg : cardBg}
                      border="2px"
                      borderColor={isSelected ? `${purpose.color}.400` : 'transparent'}
                      cursor="pointer"
                      transition="all 0.3s ease"
                      _hover={{
                        transform: 'translateY(-2px)',
                        borderColor: `${purpose.color}.300`,
                        bg: cardHoverBg
                      }}
                      onClick={() => {
                        const newPurposes = isSelected
                          ? learningPurposes.filter(p => p !== purpose.value)
                          : [...learningPurposes, purpose.value];
                        setLearningPurposes(newPurposes);
                      }}
                    >
                      <CardBody p={4}>
                        <VStack spacing={3}>
                          <Box
                            w="12"
                            h="12"
                            borderRadius="xl"
                            bgGradient={purpose.gradient}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            transition="all 0.3s ease"
                            _groupHover={{ transform: 'scale(1.1)' }}
                          >
                            <Icon as={purpose.icon} color="white" fontSize="xl" />
                          </Box>

                          <VStack spacing={1}>
                            <Text
                              fontSize="sm"
                              fontWeight="bold"
                              color={textColor}
                              textAlign="center"
                            >
                              {LEARNING_PURPOSE_LABELS[purpose.value]}
                            </Text>
                            <Checkbox
                              value={purpose.value}
                              colorScheme={purpose.color}
                              isChecked={isSelected}
                              onChange={() => {
                                const newPurposes = isSelected
                                  ? learningPurposes.filter(p => p !== purpose.value)
                                  : [...learningPurposes, purpose.value];
                                setLearningPurposes(newPurposes);
                              }}
                            />
                          </VStack>
                        </VStack>
                      </CardBody>
                    </Card>
                  );
                })}
              </SimpleGrid>
            </CheckboxGroup>
          </VStack>

          <Divider />

          {/* Time Commitment */}
          <VStack spacing={6} align="stretch">
            <VStack spacing={3} textAlign="center">
              <HStack spacing={3}>
                <Icon as={FiClock} color={accentColor} fontSize="2xl" />
                <Heading size="lg" color={textColor}>
                  {t('onboarding.goals.timeCommitment')}
                </Heading>
              </HStack>
            </VStack>

            <RadioGroup
              value={timeCommitment}
              onChange={(value) => setTimeCommitment(value as TimeCommitment)}
            >
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {timeCommitmentOptions.map((option) => {
                  const isSelected = timeCommitment === option.value;
                  return (
                    <Card
                      key={option.value}
                      bg={isSelected ? selectedCardBg : cardBg}
                      border="2px"
                      borderColor={isSelected ? `${option.color}.400` : 'transparent'}
                      cursor="pointer"
                      transition="all 0.3s ease"
                      _hover={{
                        transform: 'translateY(-2px)',
                        borderColor: `${option.color}.300`,
                        bg: cardHoverBg
                      }}
                    >
                      <CardBody p={5}>
                        <Radio value={option.value} colorScheme={option.color} w="full">
                          <HStack spacing={4} w="full">
                            <Box
                              w="10"
                              h="10"
                              borderRadius="lg"
                              bg={`${option.color}.100`}
                              color={`${option.color}.600`}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Icon as={option.icon} fontSize="lg" />
                            </Box>

                            <VStack spacing={1} align="start" flex="1">
                              <Text fontWeight="bold" color={textColor}>
                                {option.label}
                              </Text>
                              <Text fontSize="sm" color={mutedTextColor}>
                                {option.subtitle}
                              </Text>
                            </VStack>
                          </HStack>
                        </Radio>
                      </CardBody>
                    </Card>
                  );
                })}
              </SimpleGrid>
            </RadioGroup>
          </VStack>

          {/* Summary Card */}
          {targetLanguage && learningPurposes.length > 0 && (
            <Card
              bg={cardBg}
              shadow="2xl"
              borderRadius="2xl"
              border="2px"
              borderColor={successColor}
            >
              <CardBody p={6}>
                <VStack spacing={4}>
                  <HStack spacing={3}>
                    <Icon as={FiStar} color={successColor} fontSize="xl" />
                    <Heading size="md" color={textColor}>
                      {t('onboarding.goals.summary')}
                    </Heading>
                  </HStack>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                    <VStack spacing={2} align="start">
                      <Text fontSize="sm" color={textColor}>
                        <Text as="span" fontWeight="bold">{t('onboarding.goals.learning')}:</Text>{' '}
                        {selectedTargetLang?.flag} {selectedTargetLang?.label}
                      </Text>
                      <Text fontSize="sm" color={textColor}>
                        <Text as="span" fontWeight="bold">{t('onboarding.goals.timeCommitment')}:</Text>{' '}
                        {TIME_COMMITMENT_LABELS[timeCommitment]}
                      </Text>
                    </VStack>

                    <VStack spacing={2} align="start">
                      <Text fontSize="sm" color={textColor}>
                        <Text as="span" fontWeight="bold">{t('onboarding.goals.purposes')}:</Text>
                      </Text>
                      <Flex wrap="wrap" gap={2}>
                        {learningPurposes.map(purpose => {
                          const purposeOption = purposeOptions.find(p => p.value === purpose);
                          return (
                            <Badge
                              key={purpose}
                              colorScheme={purposeOption?.color || 'gray'}
                              variant="subtle"
                              fontSize="xs"
                            >
                              {LEARNING_PURPOSE_LABELS[purpose]}
                            </Badge>
                          );
                        })}
                      </Flex>
                    </VStack>
                  </SimpleGrid>
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Continue Button */}
          <Flex justify="center" pt={6}>
            <Button
              size="lg"
              onClick={(e) => {
                e.preventDefault();
                handleNext();
              }}
              isLoading={isLoading}
              loadingText={t('onboarding.saving')}
              px={12}
              py={6}
              fontSize="lg"
              fontWeight="bold"
              borderRadius="2xl"
              bgGradient="linear(to-r, blue.500, purple.500)"
              color="white"
              _hover={{
                bgGradient: "linear(to-r, blue.600, purple.600)",
                transform: "translateY(-2px)",
                shadow: "xl"
              }}
              _active={{
                transform: "translateY(0)",
              }}
              transition="all 0.3s ease"
              rightIcon={<Icon as={FiTarget} />}
            >
              {t('onboarding.goals.continue')}
            </Button>
          </Flex>
        </VStack>
      </Container>
    </Box>
  );
};

export default LanguageGoals;